import { ChangeDetectionStrategy, Component, input, signal, ViewEncapsulation } from "@angular/core";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25Util } from "../../../util/s25-util";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { FormsModule } from "@angular/forms";
import { DataAccess } from "../../../dataaccess/data.access";
import { s25Resource } from "../../../util/s25.resource";
import { EventSurveyConfig } from "../../data-ingestion/data.ingestion.service";
import { S25FadingSuccessMsgComponent } from "../../s25-fading-success-msg/s25.fading.success.msg.component";
import { AlertService } from "../../../services/alert.service";

@Component({
    standalone: true,
    selector: "s25-ng-manage-event-survey-config",
    template: `
        @if (!config()) {
            @if (readonly()) {
                <p class="span-row">No survey configured for this event</p>
            } @else {
                <s25-ng-button [type]="'primary'" [onClick]="onConfigureSurvey">Add Event Survey</s25-ng-button>
            }
        } @else {
            <label class="form-row">
                Survey Form
                <s25-ingestion-point-dropdown
                    [value]="{ itemId: $safeNavigationMigration(config()?.ingestionPointId) }"
                    [useFilter]="'eventSurvey'"
                    (valueChange)="config()!.ingestionPointId = $any($event.itemId)"
                    [readOnly]="readonly()"
                />
            </label>
            <label class="form-row">
                Days After Event End
                <input
                    type="number"
                    class="c-input"
                    [ngModel]="$safeNavigationMigration(config()!.scheduledEmail?.daysFromEventEnd)"
                    (ngModelChange)="onDaysFromEventEndChange($event)"
                    min="0"
                    required
                    placeholder="e.g., 7"
                    [disabled]="readonly()"
                />
            </label>
            @if (!readonly()) {
                <div class="form-actions">
                    <s25-ng-button [type]="'primary'" [onClick]="onSave" [isLoading]="saving()">Save</s25-ng-button>
                    <s25-ng-button [type]="'danger--outline'" [onClick]="onDelete" [isLoading]="deleting()"
                        >Delete</s25-ng-button
                    >
                </div>
            }
        }
        <s25-ng-fading-success-msg [(message)]="successMessage" [durationInMS]="5000"></s25-ng-fading-success-msg>
    `,
    styles: `
        :host {
            display: grid;
            gap: 1.25rem;
            grid-template-columns: 9em 1fr;

            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid #e8e8e8;
            padding: 1.5rem;
        }

        .form-row {
            gap: 0.5rem;
            display: grid;
            grid-template-columns: subgrid;
            grid-column: span 2;
            align-items: center;
        }

        .form-actions {
            grid-column: span 2;
            display: flex;
            gap: 0.5rem;
        }

        .span-row {
            grid-column: 1/-1;
        }

        :host-context(body.nm-party--on),
        :host-context(#s25.nm-party--on) {
            background-color: #3c3d46;
            border-color: rgba(255, 255, 255, 0.24);
            color: #fff;

            .form-row label {
                color: #fff;
            }

            .span-row {
                color: rgba(255, 255, 255, 0.73);
            }

            .c-input {
                background-color: rgb(39, 39, 41);
                border-color: rgba(255, 255, 255, 0.24);
                color: rgb(215, 218, 220);

                &:focus {
                    background-color: rgb(38, 38, 40);
                    border-color: rgb(215, 218, 220);
                }

                &::placeholder {
                    color: rgba(255, 255, 255, 0.5);
                }
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25ButtonComponent, S25DropdownPaginatedModule, FormsModule, S25FadingSuccessMsgComponent],
})
export class ManageEventSurveyConfigComponent {
    readonly eventId = input<number | undefined>();
    readonly readonly = input(false);

    readonly successMessage = signal<string>("");
    readonly saving = signal<boolean>(false);
    readonly deleting = signal<boolean>(false);

    // Resource that loads/saves the event survey config for the current event
    readonly configResource = s25Resource<EventSurveyConfig | undefined, number | undefined>({
        params: () => this.eventId(),
        loader: async ({ params: eventId, abortSignal }) => {
            if (!eventId) return undefined;
            try {
                const config = await DataAccess.get<EventSurveyConfig>(`/event-survey/config/${eventId}.json`, {
                    abortSignal,
                });
                if (config && !config.scheduledEmail) {
                    // Initialize scheduledEmail if it doesn't exist (for editing existing configs)
                    config.scheduledEmail = {
                        daysFromEventEnd: undefined,
                    };
                }
                return config || undefined;
            } catch (err: any) {
                if (err?.status === 404) {
                    return undefined;
                }
                throw err;
            }
        },
    });

    // Signal-style accessor used in the template: config()
    readonly config = this.configResource.value;

    onDaysFromEventEndChange = (value: string | number | null) => {
        const currentConfig = this.config();
        if (!currentConfig) return;

        // Convert to number (input returns string)
        const numValue = value === null || value === "" ? undefined : Number(value);

        // Create a new config object to trigger change detection
        const updatedConfig: EventSurveyConfig = {
            ...currentConfig,
            scheduledEmail: {
                ...(currentConfig.scheduledEmail || {}),
                daysFromEventEnd: numValue,
            },
        };

        // Update the resource signal
        this.configResource.resource.set(updatedConfig);
    };

    onConfigureSurvey = () => {
        this.configResource.resource.set({
            eventId: this.eventId()!,
            ingestionPointId: undefined,
            scheduledEmail: {
                daysFromEventEnd: undefined,
            },
        });
    };

    onSave = async () => {
        const eventId = this.eventId();
        const config = this.config();
        if (!eventId || !config) return;

        const daysFromEventEnd = config.scheduledEmail?.daysFromEventEnd;
        if (daysFromEventEnd === undefined || daysFromEventEnd === null) {
            S25Util.showError("Days after event end is required");
            return;
        }

        this.saving.set(true);
        try {
            const payload: EventSurveyConfig = {
                eventId: eventId,
                ingestionPointId: config.ingestionPointId,
                scheduledEmailId: config.scheduledEmailId,
                scheduledEmail: {
                    scheduledEmailId: config.scheduledEmailId,
                    daysFromEventEnd: daysFromEventEnd,
                },
            };
            await DataAccess.put(`/event-survey/config/${eventId}.json`, payload);

            // Reload the config to get the updated scheduledEmail relationship
            const updatedConfig = await DataAccess.get<EventSurveyConfig>(`/event-survey/config/${eventId}.json`);
            this.configResource.resource.set(updatedConfig);
            this.successMessage.set("Event survey configuration saved successfully");
        } catch (err: any) {
            S25Util.showError(err);
        } finally {
            this.saving.set(false);
        }
    };

    onDelete = async () => {
        const eventId = this.eventId();
        if (!eventId) return;

        const confirmed = await AlertService.confirm("Are you sure you want to delete the event survey configuration?");
        if (!confirmed) {
            return;
        }

        this.deleting.set(true);
        try {
            await DataAccess.delete(`/event-survey/config/${eventId}.json`);
            this.configResource.resource.set(undefined);
            this.successMessage.set("Event survey configuration deleted successfully");
        } catch (err: any) {
            S25Util.showError(err);
        } finally {
            this.deleting.set(false);
        }
    };
}
